import {
  enumCameraStatus,
  enumPrinterStatus,
  enumScannerStatus,
} from '@rematter/frontend-lib-api-client';

const customAttributesView = {
  entityTypes: 'Tipos de entidad',
  entityTypeLabels: {
    CoreOrganization: 'Organización',
    CoreUser: 'Usuario',
    CoreFacility: 'Instalación',
    CoreFacilityGroup: 'Grupo de Instalaciones',
    CRMAccount: 'Cuenta',
    CRMContact: 'Contacto',
    CRMAccountAddress: 'Dirección de Cuenta',
    InventoryCategory: 'Categoría',
    InventoryItemGroup: 'Grupo de Artículos',
    InventoryItem: 'Artículo',
    InventoryTag: 'Etiqueta',
    FilesFile: 'Archivo',
    FinanceFinancialAccount: 'Cuenta Financiera',
    SalesSalesOrder: 'Orden de Venta',
  },
  addCustomAttribute: 'Agregar atributo personalizado',
  creationDialog: {
    title: 'Crear Atributo Personalizado',
    fields: {
      name: 'Nombre',
      dataType: 'Tipo de Dato',
      options: 'Opciones',
      addOption: 'Agregar opción',
      objectType: 'Tipo de Objeto',
      constraints: 'Restricciones',
      allowMultipleValues: 'Permitir valores múltiples',
      required: 'Requerido',
      unique: 'Único',
      minLength: 'Longitud Mínima',
      maxLength: 'Longitud Máxima',
      minValue: 'Valor Mínimo',
      maxValue: 'Valor Máximo',
    },
    save: 'Guardar',
  },
  cards: {
    actions: {
      edit: 'Editar',
      confirmDeletion: 'Confirmar eliminación',
    },
    badges: {
      minLength: 'Longitud mínima',
      maxLength: 'Longitud máxima',
      minValue: 'Valor mínimo',
      maxValue: 'Valor máximo',
      unique: 'Único',
      multipleValues: 'Valores múltiples',
      required: 'Requerido',
    },
  },
};

const jsonView = {
  title: 'Datos de Configuración',
  subtitle:
    'Representación JSON de todos los grupos de configuración, ajustes y valores',
  toolbar: {
    refresh: 'Actualizar',
    search: 'Buscar grupos de configuración...',
  },
  content: {
    loading: 'Cargando datos de configuración...',
    error: 'Error al cargar datos de configuración',
    noData: 'No se encontraron datos de configuración',
    groupsCount: (count: number) => `${count} grupos`,
  },
  actions: {
    retry: 'Reintentar',
  },
};

const settingsView = {
  groups: 'Grupos de Configuración',
  selectGroup: 'Seleccione un grupo de configuración para ver los ajustes',
  organizationValue: 'Valor de Organización',
  scopes: 'Alcances Disponibles',
  overrides: 'Sobrecargas',
  addOverride: 'Agregar Sobrecarga',
  noOverrides: 'No hay sobrecargas configuradas',
  selectScope: 'Seleccionar alcance',
  selectEntity: 'Seleccionar entidad',
  discardChanges: 'Descartar Cambios',
  saveSettings: 'Guardar Configuración',
};

const inventoryView = {
  common: {
    cancel: 'Cancelar',
    confirmDelete: 'Eliminar',
  },
  sidebar: {
    title: 'Inventario',
  },
  sections: {
    items: 'Artículos',
    itemAliases: 'Alias de Artículos',
    categories: 'Categorías',
    itemGroups: 'Grupos de Artículos',
  },
  itemsPanel: {
    columns: {
      name: 'Nombre',
      sku: 'SKU',
      category: 'Categoría',
      itemGroup: 'Grupo de Artículo',
      description: 'Descripción',
      created: 'Creado',
    },
    search: {
      placeholder: 'Buscar artículos...',
      ariaLabel: 'Buscar artículos',
    },
    emptyState: {
      title: 'No se encontraron artículos',
      description: 'Cree artículos para gestionar su inventario.',
    },
    actions: {
      create: 'Crear Artículo',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error al cargar artículos: ${message}`,
    },
    ariaLabel: 'Lista de artículos',
  },
  itemGroupsPanel: {
    columns: {
      name: 'Nombre',
      number: 'Número',
      parentGroup: 'Grupo Padre',
      description: 'Descripción',
      created: 'Creado',
    },
    search: {
      placeholder: 'Buscar grupos de artículos...',
      ariaLabel: 'Buscar grupos de artículos',
    },
    emptyState: {
      title: 'No se encontraron grupos de artículos',
      description:
        'Cree grupos de artículos para organizar sus artículos de inventario jerárquicamente.',
    },
    actions: {
      create: 'Crear Grupo de Artículos',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error al cargar grupos de artículos: ${message}`,
    },
    ariaLabel: 'Lista de grupos de artículos',
  },
  categoriesPanel: {
    columns: {
      name: 'Nombre',
      description: 'Descripción',
      attributes: 'Atributos',
      created: 'Creado',
    },
    search: {
      placeholder: 'Buscar categorías...',
      ariaLabel: 'Buscar categorías',
    },
    emptyState: {
      title: 'No se encontraron categorías',
      description:
        'Cree categorías para clasificar sus artículos de inventario con atributos personalizados.',
    },
    actions: {
      create: 'Crear Categoría',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error al cargar categorías: ${message}`,
    },
    badges: {
      attribute: 'atributo',
      attributes: 'atributos',
    },
    ariaLabel: 'Lista de categorías',
  },
  itemAliasesPanel: {
    columns: {
      item: 'Artículo (Maestro)',
      aliasName: 'Nombre de Alias',
      aliasCode: 'Código de Alias',
      account: 'Cuenta',
      type: 'Tipo',
      notes: 'Notas',
      created: 'Creado',
    },
    search: {
      placeholder: 'Buscar alias de artículos...',
      ariaLabel: 'Buscar alias de artículos',
    },
    emptyState: {
      title: 'No se encontraron alias de artículos',
      description:
        'Cree alias de artículos para definir nombres por cuenta para los artículos.',
    },
    actions: {
      create: 'Crear Alias de Artículo',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error al cargar alias de artículos: ${message}`,
    },
    ariaLabel: 'Lista de alias de artículos',
  },
  itemCreateDialog: {
    title: 'Crear Artículo',
    sections: {
      details: 'Detalles del Artículo',
      additional: 'Información Adicional',
    },
    fields: {
      name: 'Nombre',
      skuNumber: 'Número de SKU',
      category: 'Categoría',
      itemGroup: 'Grupo de Artículo',
      pricingUnitOverride: 'Unidad de Precio (Anulación)',
      description: 'Descripción',
      notes: 'Notas',
    },
    placeholders: {
      name: 'ej., Chatarra de Aluminio Reciclado',
      skuNumber: 'ej., ALUM-001',
      category: 'Seleccionar categoría...',
      itemGroup: 'Seleccionar grupo de artículo...',
      pricingUnitOverride: 'Ninguna (heredar del grupo)',
      description: 'Descripción opcional...',
      notes: 'Notas opcionales...',
    },
    validation: {
      nameRequired: 'Por favor ingrese un nombre',
      skuRequired: 'Por favor ingrese un número de SKU',
      categoryRequired: 'Por favor seleccione una categoría',
      itemGroupRequired: 'Por favor seleccione un grupo de artículo',
    },
    messages: {
      success: 'Artículo creado exitosamente',
      error: (message: string) => `Error al crear artículo: ${message}`,
    },
    actions: {
      cancel: 'Cancelar',
      creating: 'Creando...',
      create: 'Crear Artículo',
    },
  },
  itemGroupCreateDialog: {
    title: 'Crear Grupo de Artículos',
    sections: {
      details: 'Detalles del Grupo de Artículos',
      additional: 'Información Adicional',
    },
    fields: {
      name: 'Nombre',
      number: 'Número',
      parentGroup: 'Grupo Padre',
      defaultPricingUnit: 'Unidad de Precio Predeterminada',
      description: 'Descripción',
      notes: 'Notas',
    },
    placeholders: {
      name: 'ej., Productos de Aluminio',
      number: 'ej., ALU-GRP',
      parentGroup: 'Seleccionar grupo padre (opcional)...',
      defaultPricingUnit: 'Ninguna (predeterminado a Libras)',
      description: 'Descripción opcional...',
      notes: 'Notas opcionales...',
    },
    validation: {
      nameRequired: 'Por favor ingrese un nombre',
      numberRequired: 'Por favor ingrese un número',
    },
    messages: {
      success: 'Grupo de artículos creado exitosamente',
      error: (message: string) =>
        `Error al crear grupo de artículos: ${message}`,
    },
    actions: {
      cancel: 'Cancelar',
      creating: 'Creando...',
      create: 'Crear Grupo de Artículos',
    },
  },
  categoryCreateDialog: {
    title: 'Crear Categoría',
    sections: {
      details: 'Detalles de la Categoría',
      attributes: 'Atributos',
    },
    fields: {
      name: 'Nombre',
      description: 'Descripción',
      attributeName: 'Nombre del Atributo',
      dataType: 'Tipo de Dato',
      options: 'Opciones (separadas por coma)',
    },
    placeholders: {
      name: 'ej., Metales Ferrosos',
      description: 'Descripción opcional...',
      attributeName: 'ej., Grado',
      attributeDescription: 'Descripción opcional...',
      options: 'ej., Opción A, Opción B, Opción C',
    },
    validation: {
      nameRequired: 'Por favor ingrese un nombre',
      attributeNameRequired:
        'Por favor ingrese un nombre para todos los atributos',
    },
    messages: {
      success: 'Categoría creada exitosamente',
      error: (message: string) => `Error al crear categoría: ${message}`,
      noAttributes:
        'No hay atributos definidos. Agregue atributos para definir campos personalizados para los artículos en esta categoría.',
    },
    attributes: {
      required: 'Requerido',
      removeAriaLabel: 'Eliminar atributo',
    },
    actions: {
      addAttribute: 'Agregar Atributo',
      cancel: 'Cancelar',
      creating: 'Creando...',
      create: 'Crear Categoría',
    },
  },
  itemEditDialog: {
    title: 'Editar Artículo',
    sections: {
      details: 'Detalles del Artículo',
      additional: 'Información Adicional',
    },
    fields: {
      name: 'Nombre',
      skuNumber: 'Número de SKU',
      category: 'Categoría',
      itemGroup: 'Grupo de Artículo',
      pricingUnitOverride: 'Unidad de Precio (Anulación)',
      description: 'Descripción',
      notes: 'Notas',
    },
    placeholders: {
      name: 'ej., Chatarra de Aluminio Reciclado',
      skuNumber: 'ej., ALUM-001',
      category: 'Seleccionar categoría...',
      itemGroup: 'Seleccionar grupo de artículo...',
      pricingUnitOverride: 'Ninguna (heredar del grupo)',
      description: 'Descripción opcional...',
      notes: 'Notas opcionales...',
    },
    validation: {
      nameRequired: 'Por favor ingrese un nombre',
      skuRequired: 'Por favor ingrese un número de SKU',
      categoryRequired: 'Por favor seleccione una categoría',
      itemGroupRequired: 'Por favor seleccione un grupo de artículo',
    },
    messages: {
      success: 'Artículo actualizado exitosamente',
      error: (message: string) => `Error al actualizar artículo: ${message}`,
    },
    actions: {
      cancel: 'Cancelar',
      saving: 'Guardando...',
      save: 'Guardar Cambios',
    },
  },
  itemDetailView: {
    sections: {
      itemInformation: 'Información del Artículo',
      timestamps: 'Marcas de Tiempo',
      description: 'Descripción',
      notes: 'Notas',
      attributes: 'Atributos',
    },
    fields: {
      sku: 'SKU',
      category: 'Categoría',
      itemGroup: 'Grupo de Artículo',
      created: 'Creado',
      updated: 'Actualizado',
    },
    tabs: {
      details: 'Detalles',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToItems: 'Volver a Artículos',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Actualizaciones',
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar este artículo? Esta acción no se puede deshacer.',
      deleteSuccess: 'Artículo eliminado exitosamente',
      deleteError: (message: string) =>
        `Error al eliminar artículo: ${message}`,
      itemNotFound: 'Artículo no encontrado',
      unsavedDataMessage:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Artículo actualizado exitosamente',
      updateError: (message: string) =>
        `Error al actualizar artículo: ${message}`,
    },
  },
  itemAliasCreateDialog: {
    title: 'Crear Alias de Artículo',
    sections: {
      details: 'Detalles del Alias',
      additional: 'Información Adicional',
    },
    fields: {
      item: 'Artículo',
      account: 'Cuenta',
      aliasName: 'Nombre de Alias',
      aliasCode: 'Código de Alias',
      type: 'Tipo',
      notes: 'Notas',
    },
    placeholders: {
      item: 'Seleccionar un artículo...',
      account: 'Seleccionar una cuenta...',
      aliasName: 'Como la cuenta llama a este artículo...',
      aliasCode: 'Código opcional...',
      notes: 'Notas opcionales...',
    },
    aliasTypes: {
      buy: 'Compra (de proveedores)',
      sell: 'Venta (a clientes)',
    },
    validation: {
      itemRequired: 'Por favor seleccione un artículo',
      accountRequired: 'Por favor seleccione una cuenta',
      aliasNameRequired: 'Por favor ingrese un nombre de alias',
    },
    messages: {
      success: 'Alias de artículo creado exitosamente',
      error: (message: string) =>
        `Error al crear alias de artículo: ${message}`,
    },
    actions: {
      cancel: 'Cancelar',
      creating: 'Creando...',
      create: 'Crear Alias de Artículo',
    },
  },
  itemGroupDetailView: {
    sections: {
      itemGroupInformation: 'Información del Grupo de Artículos',
      timestamps: 'Marcas de Tiempo',
      additional: 'Información Adicional',
    },
    fields: {
      number: 'Número',
      parentGroup: 'Grupo Padre',
      created: 'Creado',
      updated: 'Actualizado',
    },
    tabs: {
      details: 'Detalles',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToItemGroups: 'Volver a Grupos de Artículos',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Actualizaciones',
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar este grupo de artículos? Esta acción no se puede deshacer.',
      deleteSuccess: 'Grupo de artículos eliminado exitosamente',
      deleteError: (message: string) =>
        `Error al eliminar grupo de artículos: ${message}`,
      itemGroupNotFound: 'Grupo de artículos no encontrado',
      unsavedDataMessage:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Grupo de artículos actualizado exitosamente',
      updateError: (message: string) =>
        `Error al actualizar grupo de artículos: ${message}`,
    },
  },
  itemAliasDetailView: {
    sections: {
      aliasAssociation: 'Asociación de Alias',
      aliasInformation: 'Información del Alias',
      timestamps: 'Marcas de Tiempo',
      additional: 'Información Adicional',
    },
    fields: {
      created: 'Creado',
      updated: 'Actualizado',
    },
    tabs: {
      details: 'Detalles',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToItemAliases: 'Volver a Alias de Artículos',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Actualizaciones',
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar este alias de artículo? Esta acción no se puede deshacer.',
      deleteSuccess: 'Alias de artículo eliminado exitosamente',
      deleteError: (message: string) =>
        `Error al eliminar alias de artículo: ${message}`,
      itemAliasNotFound: 'Alias de artículo no encontrado',
      unsavedDataMessage:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Alias de artículo actualizado exitosamente',
      updateError: (message: string) =>
        `Error al actualizar alias de artículo: ${message}`,
    },
  },
  categoryDetailView: {
    sections: {
      categoryInformation: 'Información de la Categoría',
      timestamps: 'Marcas de Tiempo',
      attributes: 'Atributos',
    },
    fields: {
      created: 'Creado',
      updated: 'Actualizado',
    },
    tabs: {
      details: 'Detalles',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToCategories: 'Volver a Categorías',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Actualizaciones',
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar esta categoría? Esta acción no se puede deshacer.',
      deleteSuccess: 'Categoría eliminada exitosamente',
      deleteError: (message: string) =>
        `Error al eliminar categoría: ${message}`,
      categoryNotFound: 'Categoría no encontrada',
      unsavedDataMessage:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Categoría actualizada exitosamente',
      updateError: (message: string) =>
        `Error al actualizar categoría: ${message}`,
    },
  },
};

const complianceView = {
  common: {
    actions: {
      edit: 'Editar',
      delete: 'Eliminar',
      viewDetails: 'Ver Detalles',
      cancel: 'Cancelar',
    },
    confirmDelete: '¿Está seguro?',
    yesDelete: 'Sí, eliminar',
    yes: 'Sí',
    no: 'No',
    loading: 'Cargando...',
  },
  rules: {
    title: 'Configuración de Reglas',
    subtitle: 'Configurar reglas de cumplimiento por instalación',
    facilityList: {
      columns: {
        name: 'Instalación',
        type: 'Tipo',
        activeRules: 'Reglas Activas',
      },
      search: { placeholder: 'Buscar instalaciones...' },
      emptyState: {
        title: 'No se encontraron instalaciones',
        description:
          'Cree instalaciones en la configuración de Ubicaciones primero.',
      },
    },
    facilityRules: {
      title: 'Reglas de Instalación',
      subtitle: 'Gestionar reglas de cumplimiento para esta instalación',
      columns: {
        rule: 'Regla',
        category: 'Categoría',
        enforcement: 'Aplicación',
        value: 'Valor',
        enabled: 'Habilitado',
      },
      actions: {
        addRule: 'Agregar Regla',
        applyJurisdictionPack: 'Aplicar Paquete de Jurisdicción',
      },
      emptyState: {
        title: 'Sin reglas configuradas',
        description:
          'Agregue reglas individualmente o aplique un paquete de jurisdicción para comenzar.',
      },
    },
    dialog: {
      createTitle: 'Agregar Regla',
      editTitle: 'Editar Regla',
      sections: {
        rule: 'Regla',
      },
      fields: {
        ruleDefinition: 'Definición de Regla',
        isEnabled: 'Habilitado',
        configuredValue: 'Valor',
        enforcementLevelOverride: 'Nivel de Aplicación',
        notes: 'Notas',
      },
      placeholders: {
        ruleDefinition: 'Seleccionar una regla...',
        notes: 'Notas opcionales...',
        booleanValue: 'verdadero / falso',
        integerValue: 'ej., 7',
        decimalValue: 'ej., 25.00',
        defaultValue: 'Valor...',
        enforcementLevel: 'Usar Predeterminado',
      },
      ruleInfo: {
        type: 'Tipo',
        enforcement: 'Aplicación',
      },
      actions: { create: 'Agregar Regla', save: 'Guardar Cambios' },
      messages: {
        createSuccess: 'Regla agregada exitosamente',
        createError: (message: string) => `Error al agregar regla: ${message}`,
        updateSuccess: 'Regla actualizada exitosamente',
        updateError: (message: string) =>
          `Error al actualizar regla: ${message}`,
        deleteSuccess: 'Regla eliminada exitosamente',
        deleteError: (message: string) => `Error al eliminar regla: ${message}`,
      },
    },
    jurisdictionPacks: {
      title: 'Aplicar Paquete de Jurisdicción',
      description:
        'Aplicar un conjunto preconfigurado de reglas para una jurisdicción específica.',
      jurisdictions: {
        CA: 'California',
        TX: 'Texas',
        FL: 'Florida',
        OH: 'Ohio',
        DEFAULT: 'Predeterminado (EE.UU.)',
      },
      actions: { apply: 'Aplicar Paquete' },
      messages: {
        applySuccess: (jurisdiction: string) =>
          `Paquete de jurisdicción ${jurisdiction} aplicado exitosamente`,
        applyError: (message: string) =>
          `Error al aplicar paquete de jurisdicción: ${message}`,
        noMatchingRules:
          'No se encontraron definiciones de reglas coincidentes para este paquete de jurisdicción',
      },
    },
  },
  doNotBuy: {
    title: 'Lista de No Comprar',
    subtitle: 'Gestionar vendedores bloqueados',
    columns: {
      account: 'Cuenta',
      reason: 'Razón',
      status: 'Estado',
      addedBy: 'Agregado Por',
      effectiveFrom: 'Vigente Desde',
    },
    search: { placeholder: 'Buscar entradas...' },
    emptyState: {
      title: 'Sin entradas de no comprar',
      description:
        'Agregue cuentas para prevenir transacciones con vendedores bloqueados.',
    },
    actions: { addEntry: 'Agregar Entrada' },
    dialog: {
      createTitle: 'Agregar Entrada de No Comprar',
      editTitle: 'Editar Entrada de No Comprar',
      sections: {
        entryDetails: 'Detalles de Entrada',
      },
      fields: {
        account: 'Cuenta',
        facility: 'Instalación (Opcional)',
        reason: 'Razón',
        originType: 'Tipo de Origen',
        effectiveFrom: 'Vigente Desde',
        effectiveTo: 'Vigente Hasta (Opcional)',
        notes: 'Notas',
      },
      placeholders: {
        account: 'Buscar cuentas...',
        facility: 'Todas las instalaciones',
        reason: 'Razón del bloqueo...',
        originType: 'Seleccionar tipo de origen...',
        notes: 'Notas opcionales...',
      },
      originTypes: {
        Manual: 'Manual',
        Automated: 'Automatizado',
        LawEnforcement: 'Policía',
      },
      actions: { create: 'Agregar Entrada', save: 'Guardar Cambios' },
      messages: {
        createSuccess: 'Entrada creada exitosamente',
        createError: (message: string) => `Error al crear entrada: ${message}`,
        updateSuccess: 'Entrada actualizada exitosamente',
        updateError: (message: string) =>
          `Error al actualizar entrada: ${message}`,
        deleteSuccess: 'Entrada eliminada exitosamente',
        deleteError: (message: string) =>
          `Error al eliminar entrada: ${message}`,
      },
    },
  },
  serviceTypeLabels: {
    LeadsOnline: 'LeadsOnline',
    LeadsOnlineCanada: 'LeadsOnline Canadá',
    TexasOnlineMetals: 'Texas Online Metals',
    Kansas: 'Kansas',
    OhioDPS: 'Ohio DPS',
    Alabama: 'Alabama',
    CanadaBWI: 'Canadá BWI',
    BCPolice: 'Policía BC',
    WAPolice: 'Policía WA',
    NMVTIS: 'NMVTIS',
  } as Record<string, string>,
  govReporting: {
    title: 'Reportes Gubernamentales',
    subtitle: 'Configurar servicios de reportes gubernamentales',
    columns: {
      name: 'Nombre',
      serviceType: 'Tipo de Servicio',
      uploadType: 'Tipo de Carga',
      active: 'Activo',
    },
    search: { placeholder: 'Buscar configuraciones...' },
    emptyState: {
      title: 'Sin configuraciones de reportes',
      description:
        'Agregue configuraciones de servicios de reportes gubernamentales.',
    },
    actions: { addConfig: 'Agregar Configuración' },
    dialog: {
      createTitle: 'Agregar Configuración de Reportes',
      editTitle: 'Editar Configuración de Reportes',
      sections: {
        configuration: 'Configuración',
      },
      fields: {
        name: 'Nombre',
        serviceType: 'Tipo de Servicio',
        uploadType: 'Tipo de Carga',
        recyclerId: 'ID de Reciclador',
        toEmailAddress: 'Correo Electrónico',
        uploadTimezone: 'Zona Horaria',
        daysOfWeek: 'Días de la Semana',
        encryptedCredentials: 'Credenciales',
        processBatchesAsUnit: 'Procesar Lotes como Unidad',
        isActive: 'Activo',
      },
      uploadTypes: {
        Automatic: 'Automático',
        Manual: 'Manual',
      },
      placeholders: {
        name: 'ej., Reportes de Texas',
        serviceType: 'Seleccionar servicio...',
        uploadType: 'Seleccionar tipo...',
        recyclerId: 'ID opcional...',
        toEmailAddress: 'destinatario@ejemplo.com',
        uploadTimezone: 'ej., America/Chicago',
        encryptedCredentials: 'Clave API o contraseña...',
      },
      actions: { create: 'Crear Configuración', save: 'Guardar Cambios' },
      messages: {
        createSuccess: 'Configuración creada exitosamente',
        createError: (message: string) =>
          `Error al crear configuración: ${message}`,
        updateSuccess: 'Configuración actualizada exitosamente',
        updateError: (message: string) =>
          `Error al actualizar configuración: ${message}`,
        deleteSuccess: 'Configuración eliminada exitosamente',
        deleteError: (message: string) =>
          `Error al eliminar configuración: ${message}`,
      },
    },
    detail: {
      subtitle: 'Detalles y programación de la configuración',
      sections: {
        configInfo: 'Configuración',
        schedule: 'Programación',
        facilities: 'Instalaciones Vinculadas',
        fieldSelection: 'Selección de Campos',
      },
      fields: {
        serviceType: 'Tipo de Servicio',
        uploadType: 'Tipo de Carga',
        recyclerId: 'ID de Reciclador',
        email: 'Correo Electrónico',
        timezone: 'Zona Horaria',
        batchProcessing: 'Procesamiento por Lotes',
        active: 'Activo',
        created: 'Creado',
        updated: 'Actualizado',
        daysOfWeek: 'Días de la Semana',
        credentials: 'Credenciales',
      },
      facilityAssignment: {
        columns: {
          facility: 'Instalación',
          reportingFacilityId: 'ID de Reporte',
          mlid: 'MLID',
        },
        actions: {
          addFacility: 'Agregar Instalación',
        },
        emptyState: {
          title: 'Sin instalaciones asignadas',
          description:
            'Asigne instalaciones a esta configuración para habilitar el despacho.',
        },
        dialog: {
          createTitle: 'Asignar Instalación',
          editTitle: 'Editar Asignación de Instalación',
          fields: {
            facility: 'Instalación',
            reportingFacilityId: 'ID de Instalación de Reporte',
            mlid: 'MLID',
          },
          placeholders: {
            facility: 'Seleccionar una instalación...',
            reportingFacilityId: 'ID de reporte opcional...',
            mlid: 'MLID opcional...',
          },
          actions: {
            create: 'Asignar Instalación',
            save: 'Guardar Cambios',
          },
          messages: {
            createSuccess: 'Instalación asignada exitosamente',
            createError: (message: string) =>
              `Error al asignar instalación: ${message}`,
            updateSuccess: 'Asignación actualizada exitosamente',
            updateError: (message: string) =>
              `Error al actualizar asignación: ${message}`,
            deleteSuccess: 'Instalación desasignada exitosamente',
            deleteError: (message: string) =>
              `Error al desasignar instalación: ${message}`,
          },
        },
      },
      fieldSelection: {
        groups: {
          scaleTicket: 'Ticket de Báscula',
          publicSupplier: 'Proveedor Público',
          vehicle: 'Vehículo',
          material: 'Material',
        },
        fields: {
          ticketNumber: 'Número de Ticket',
          ticketDate: 'Fecha del Ticket',
          grossWeight: 'Peso Bruto',
          tareWeight: 'Peso Tara',
          netWeight: 'Peso Neto',
          supplierName: 'Nombre del Proveedor',
          supplierAddress: 'Dirección',
          supplierPhone: 'Teléfono',
          supplierIdType: 'Tipo de ID',
          supplierId: 'ID del Proveedor',
          vehicleMake: 'Marca',
          vehicleModel: 'Modelo',
          vehicleYear: 'Año',
          vehiclePlate: 'Placa',
          vehicleVin: 'VIN',
          materialDescription: 'Descripción',
          materialCode: 'Código',
          materialQuantity: 'Cantidad',
          materialUnit: 'Unidad',
        },
      },
      dayLabels: {
        monday: 'Lun',
        tuesday: 'Mar',
        wednesday: 'Mié',
        thursday: 'Jue',
        friday: 'Vie',
        saturday: 'Sáb',
        sunday: 'Dom',
      },
      notFound: 'Configuración no encontrada',
      actions: {
        backToList: 'Volver a Configuraciones',
        viewDispatchHistory: 'Ver Historial de Despachos',
      },
    },
  },
  dispatchHistory: {
    title: 'Historial de Despachos',
    subtitle: 'Monitorear registros de despacho de reportes gubernamentales',
    columns: {
      facility: 'Instalación',
      configuration: 'Configuración',
      serviceType: 'Servicio',
      status: 'Estado',
      batchId: 'ID de Lote',
      submittedAt: 'Enviado',
      errorMessage: 'Error',
      retries: 'Reintentos',
    },
    search: {
      placeholder: 'Buscar registros de despacho...',
    },
    filters: {
      status: 'Estado',
      configuration: 'Configuración',
      allStatuses: 'Todos los Estados',
      allConfigurations: 'Todas las Configuraciones',
    },
    statuses: {
      Queued: 'En Cola',
      Processing: 'Procesando',
      Submitted: 'Enviado',
      Failed: 'Fallido',
      NotReported: 'No Reportado',
    },
    emptyState: {
      title: 'Sin registros de despacho',
      description:
        'Los registros de despacho aparecerán aquí cuando los reportes gubernamentales estén configurados y en ejecución.',
    },
    actions: {
      retry: 'Reintentar',
    },
    messages: {
      retrySuccess: 'Registro de despacho en cola para reintento',
      retryError: (message: string) =>
        `Error al reintentar registro: ${message}`,
    },
  },
  itemCodes: {
    title: 'Códigos de Artículos',
    subtitle: 'Gestionar códigos de clasificación gubernamental por artículo',
    columns: {
      item: 'Artículo',
      serviceType: 'Tipo de Servicio',
      code: 'Código',
      reportable: 'Reportable',
    },
    search: { placeholder: 'Buscar códigos...' },
    emptyState: {
      title: 'Sin códigos de jurisdicción',
      description:
        'Agregue códigos de jurisdicción para clasificar artículos para reportes gubernamentales.',
    },
    actions: { addCode: 'Agregar Código' },
    dialog: {
      createTitle: 'Agregar Código de Jurisdicción',
      editTitle: 'Editar Código de Jurisdicción',
      sections: {
        jurisdictionCode: 'Código de Jurisdicción',
      },
      fields: {
        item: 'Artículo',
        serviceType: 'Tipo de Servicio',
        code: 'Código',
        isReportable: 'Reportable',
      },
      placeholders: {
        item: 'Seleccionar un artículo...',
        serviceType: 'Seleccionar servicio...',
        code: 'ej., 0001',
      },
      actions: { create: 'Agregar Código', save: 'Guardar Cambios' },
      messages: {
        createSuccess: 'Código creado exitosamente',
        createError: (message: string) => `Error al crear código: ${message}`,
        updateSuccess: 'Código actualizado exitosamente',
        updateError: (message: string) =>
          `Error al actualizar código: ${message}`,
        deleteSuccess: 'Código eliminado exitosamente',
        deleteError: (message: string) =>
          `Error al eliminar código: ${message}`,
      },
    },
  },
};

const preferencesView = {
  title: 'Preferencias',
  language: {
    label: 'Idioma',
    description: 'Elija el idioma para la interfaz de la aplicación',
  },
  moduleOrder: {
    title: 'Orden de módulos en la barra lateral',
    description:
      'Arrastra y suelta para reordenar los módulos en la barra lateral',
    resetToDefault: 'Restablecer al predeterminado',
    setAsLandingPage: 'Establecer como página de inicio predeterminada',
    defaultLandingPage: 'Página de inicio predeterminada',
    landingPageSet: (moduleName: string) =>
      `Página predeterminada establecida en ${moduleName}`,
    landingPageCleared: 'Página predeterminada eliminada',
    messages: {
      saveError: 'Error al guardar el orden de módulos',
      landingPageError:
        'Error al establecer la página de inicio predeterminada',
    },
  },
  messages: {
    saveSuccess: 'Preferencia de idioma guardada',
    saveError: 'Error al guardar la preferencia de idioma',
  },
};

const teamAndPermissions = {
  title: 'Gestión del equipo',
  subtitle: 'Agregar y gestionar miembros del equipo y accesos',
  noPermission: 'No tienes permiso para acceder a esta sección.',
  tabs: {
    facilities: 'Ubicaciones',
    roles: 'Roles',
    users: 'Usuarios',
  },
  search: {
    placeholder: 'Buscar',
  },
  emptyState: {
    facilities: 'No se encontraron ubicaciones',
    roles: 'No se encontraron roles',
    users: 'No se encontraron usuarios',
  },
  actions: {
    addRole: 'Agregar rol',
    addUser: 'Agregar usuario',
    allUsersAdded: 'Todos los usuarios han sido agregados',
    discard: 'Descartar',
    save: 'Guardar cambios',
  },
  addUserDialog: {
    title: 'Agregar usuario',
    steps: {
      userInfo: 'Info del usuario',
      roleAssignment: 'Roles',
    },
    fields: {
      firstName: {
        label: 'Nombre',
        placeholder: 'Nombre',
      },
      lastName: {
        label: 'Apellido',
        placeholder: 'Apellido',
      },
      email: {
        label: 'Correo electrónico',
        placeholder: 'Dirección de correo',
      },
      phoneNumber: {
        label: 'Teléfono',
        placeholder: '+1 (555) 000-0000',
      },
    },
    roleStep: {
      searchRolesPlaceholder: 'Buscar roles...',
      searchFacilitiesPlaceholder: 'Buscar ubicaciones...',
      rolesHeader: 'Roles',
      facilitiesHeader: 'Ubicaciones',
      selectedAssignments: 'Asignaciones seleccionadas',
      noRolesFound: 'No se encontraron roles',
      noFacilitiesFound: 'No se encontraron ubicaciones',
      assignmentCount: (count: number) =>
        `${count} asignación${count !== 1 ? 'es' : ''}`,
      selectRolePrompt: 'Seleccione un rol para asignar ubicaciones',
      loading: 'Cargando...',
      clearButton: 'Limpiar',
    },
    actions: {
      cancel: 'Cancelar',
      sendInvite: 'Enviar invitación',
      next: 'Siguiente',
      back: 'Atrás',
    },
    messages: {
      success: 'Invitación enviada correctamente',
      error: 'Error al enviar la invitación',
    },
  },
  facilities: {
    table: {
      columns: {
        name: 'Nombre de ubicación',
        address: 'Dirección',
        users: 'Usuarios',
        roles: 'Roles activos',
      },
      aria: 'Tabla de ubicaciones',
    },
    emptyState: {
      title: 'No se encontraron ubicaciones',
      subtitle:
        'No hay ubicaciones que coincidan con los criterios de búsqueda.',
    },
    details: {
      tabs: {
        roles: 'Roles',
        users: 'Usuarios',
      },
      actions: {
        goToRoleSettings: 'Ir a configuración de roles',
        goToUserSettings: 'Ir a configuración del usuario',
      },
      sections: {
        users: 'Usuarios',
        roles: 'Roles',
        permissions: 'Permisos',
      },
      roles: {
        emptyState: 'Seleccionar un rol',
        emptyStateSubtitle:
          'Elija un rol de la lista para ver usuarios y permisos en esta ubicación',
      },
      users: {
        columns: {
          name: 'Nombre',
          email: 'Correo electrónico',
          phone: 'Teléfono',
        },
        emptyState: {
          title: 'No hay usuarios en esta ubicación',
          subtitle:
            'Los usuarios asignados a roles en esta ubicación aparecerán aquí.',
        },
        selectEmptyState: 'Seleccionar un usuario',
        selectEmptyStateSubtitle:
          'Elija un usuario de la lista para ver sus roles y permisos en esta ubicación',
        removeAccess: 'Quitar acceso',
        rolesUpdated: 'Roles de usuario actualizados correctamente',
        rolesUpdateError: 'Error al actualizar los roles del usuario',
        removeFromFacilityDialog: {
          title: (name: string) => `¿Quitar a ${name} de esta ubicación?`,
          description: (name: string) =>
            `Después de confirmar, ${name} será eliminado de esta ubicación. Esta acción se puede deshacer reasignando al usuario.`,
          confirm: 'Quitar',
          cancel: 'Cancelar',
          success: 'Usuario eliminado de la ubicación',
          error: 'Error al eliminar usuario de la ubicación',
        },
      },
    },
  },
  roles: {
    table: {
      columns: {
        name: 'Nombre del rol',
        users: 'Usuarios',
        status: 'Estado',
        actions: 'Acciones',
      },
      actions: {
        archiveRole: 'Archivar rol',
        restoreRole: 'Restaurar rol',
      },
      showArchived: 'Mostrar archivados',
      aria: 'Tabla de roles',
    },
    emptyState: {
      title: 'No se encontraron roles',
      subtitle: 'No hay roles que coincidan con los criterios de búsqueda.',
    },
    details: {
      tabs: {
        permissions: 'Permisos',
        users: 'Usuarios',
      },
      actions: {
        rename: 'Renombrar',
        archive: 'Archivar',
        restore: 'Restaurar',
      },
      users: {
        viewUserSettings: 'Ver configuración del usuario',
        removeFromRole: 'Quitar del rol',
        removeFromRoleDialog: {
          title: (name: string) => `¿Quitar a ${name} de este rol?`,
          description: (name: string) =>
            `Después de confirmar, ${name} será eliminado de este rol. Esta acción se puede deshacer reasignando el rol.`,
          confirm: 'Quitar',
          cancel: 'Cancelar',
          success: 'Usuario eliminado del rol',
          error: 'Error al eliminar usuario del rol',
        },
        bulk: {
          selected: 'seleccionados',
          removeFromRole: 'Quitar del rol',
          resendInvite: 'Reenviar invitación',
          bulkRemoveDialog: {
            title: (count: number) =>
              `¿Quitar ${count} usuario${count === 1 ? '' : 's'} de este rol?`,
            description: (count: number) =>
              `Después de confirmar, ${count} usuario${count === 1 ? '' : 's'} será${count === 1 ? '' : 'n'} eliminado${count === 1 ? '' : 's'} de este rol. Esta acción se puede deshacer reasignando el rol.`,
            confirm: 'Quitar',
            cancel: 'Cancelar',
          },
          removeSuccess: (count: number) =>
            `${count} usuario${count === 1 ? '' : 's'} eliminado${count === 1 ? '' : 's'} del rol`,
          removeError: 'Error al eliminar usuarios del rol',
        },
      },
    },
    renameDialog: {
      title: 'Renombrar rol',
      label: 'Nombre del rol',
      placeholder: 'Ingrese el nombre del rol',
      cancel: 'Cancelar',
      save: 'Guardar',
      success: 'Rol renombrado correctamente',
      error: 'Error al renombrar el rol',
    },
    addRoleDialog: {
      title: 'Crear rol',
      fields: {
        name: {
          label: 'Nombre del rol',
          placeholder: 'Ingrese el nombre del rol',
        },
      },
      actions: {
        cancel: 'Cancelar',
        save: 'Guardar rol',
      },
      messages: {
        success: 'Rol creado correctamente',
        error: 'Error al crear el rol',
      },
    },
    archivedReadOnlyMessage:
      'Los roles archivados no se pueden editar. Restaure el rol para hacer cambios.',
    dialogs: {
      archive: {
        title: (name: string) => `¿Archivar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, el rol "${name}" será archivado. Los usuarios asignados a este rol perderán los permisos asociados. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Archivar',
        success: (name: string) => `El rol ${name} ha sido archivado`,
      },
      restore: {
        title: (name: string) => `¿Restaurar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, el rol "${name}" será restaurado y volverá a estar activo. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Restaurar',
        success: (name: string) => `El rol ${name} ha sido restaurado`,
      },
      bulkArchive: {
        title: (count: number) =>
          `¿Archivar ${count} rol${count === 1 ? '' : 'es'}?`,
        description: (count: number) =>
          `Tras confirmar, ${count} rol${count === 1 ? '' : 'es'} será${count === 1 ? '' : 'n'} archivado${count === 1 ? '' : 's'}. Los usuarios asignados a estos roles perderán los permisos asociados. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Archivar',
        success: (count: number) =>
          `${count} rol${count === 1 ? '' : 'es'} archivado${count === 1 ? '' : 's'}`,
      },
      bulkRestore: {
        title: (count: number) =>
          `¿Restaurar ${count} rol${count === 1 ? '' : 'es'}?`,
        description: (count: number) =>
          `Tras confirmar, ${count} rol${count === 1 ? '' : 'es'} será${count === 1 ? '' : 'n'} restaurado${count === 1 ? '' : 's'} y volverá${count === 1 ? '' : 'n'} a estar activo${count === 1 ? '' : 's'}. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Restaurar',
        success: (count: number) =>
          `${count} rol${count === 1 ? '' : 'es'} restaurado${count === 1 ? '' : 's'}`,
      },
    },
    bulk: {
      selected: 'seleccionados',
      archive: 'Archivar',
      restore: 'Restaurar',
    },
    addUsersDialog: {
      title: 'Agregar usuario(s) al rol {roleName}',
      steps: {
        users: 'Usuarios',
        facilities: 'Ubicaciones',
      },
      users: {
        header: 'USUARIOS',
        searchPlaceholder: 'Buscar',
      },
      facilities: {
        header: 'UBICACIONES',
        searchPlaceholder: 'Buscar',
      },
      actions: {
        clear: 'Limpiar',
        next: 'Siguiente',
        back: 'Atrás',
        save: 'Guardar',
        addUser: 'Agregar usuario ({count})',
      },
      messages: {
        success: 'Usuarios agregados al rol correctamente',
        error: 'Error al agregar usuarios al rol',
        partialSuccess:
          '{successCount} de {totalCount} usuarios agregados correctamente',
      },
    },
  },
  userStatus: {
    active: 'Activo',
    archived: 'Archivado',
    inviteSent: 'Invitación enviada',
  },
  roleStatus: {
    active: 'Activo',
    archived: 'Archivado',
  },
  users: {
    selected: 'seleccionados',
    table: {
      columns: {
        name: 'Nombre',
        contactInfo: 'Información de contacto',
        facilities: 'Ubicaciones',
        status: 'Estado',
      },
      aria: 'Tabla de usuarios',
    },
    filters: {
      allFacilities: 'Todas las ubicaciones',
      allRoles: 'Todos los roles',
    },
    actions: {
      edit: 'Editar',
      resendInvite: 'Reenviar invitación',
      removeAccess: 'Quitar acceso',
      restoreAccess: 'Restaurar acceso',
      deleteUser: 'Eliminar usuario',
    },
    emptyState: {
      title: 'No se encontraron usuarios',
      subtitle: 'No hay usuarios que coincidan con los criterios de búsqueda.',
    },
    dialogs: {
      removeAccess: {
        title: (name: string) => `¿Quitar acceso a ${name}?`,
        description: (name: string) =>
          `Tras confirmar, ${name} perderá el acceso al sistema. ¿Desea continuar?`,
        discard: 'Descartar',
        remove: 'Quitar',
      },
      restoreAccess: {
        title: (name: string) => `¿Restaurar acceso a ${name}?`,
        description: (name: string) =>
          `Tras confirmar, el acceso de ${name} con todos los permisos asignados será restaurado. ¿Desea continuar?`,
        discard: 'Descartar',
        restore: 'Restaurar',
      },
      deleteAccount: {
        title: (name: string) => `¿Eliminar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, ${name} será eliminado y dejará de existir en la aplicación. Para confirmar la eliminación, escriba el nombre del miembro del equipo.`,
        discard: 'Descartar',
        delete: 'Eliminar',
        teamMemberNameLabel: 'Nombre del miembro del equipo',
      },
    },
  },
  userDetails: {
    loading: 'Cargando usuario...',
    contactInfo: {
      sectionTitle: 'Info de contacto',
      fields: {
        firstName: 'Nombre',
        lastName: 'Apellido',
        email: 'Correo electrónico',
        phone: 'Teléfono',
      },
      accessCode: {
        sectionTitle: 'Código de acceso',
        subtitle:
          'Se utiliza como contraseña de respaldo para iniciar sesión en el sistema',
        codeSentToPrefix: 'El código se enviará a',
        codeSentToOr: 'o',
        codeLabel: 'Código',
        sendViaEmail: 'Enviar por correo',
        sendViaText: 'Enviar por texto',
      },
      messages: {
        updateSuccess: 'Usuario actualizado correctamente',
        updateError: 'Error al actualizar usuario',
        codeRegenerated: 'Código de acceso regenerado',
        codeRegenerateError: 'Error al regenerar código de acceso',
        codeSentViaEmail: 'Código de acceso enviado por correo',
        codeSentViaText: 'Código de acceso enviado por texto',
      },
    },
    roles: {
      searchFacilitiesAriaLabel: 'Buscar ubicaciones',
      allFacilities: (count: number) => `Todas las ubicaciones (${count})`,
      assignFacilityAriaLabel: (name: string) => `Asignar ${name}`,
      selectAllFacilitiesAriaLabel: 'Seleccionar todas las ubicaciones',
      lastUpdated: (date: string, user: string) =>
        `Última actualización el ${date} por ${user}`,
      emptyState: {
        title: 'Seleccionar un rol',
        subtitle: 'Elija un rol de la lista para asignar ubicaciones',
      },
      messages: {
        updateSuccess: 'Ubicaciones del rol actualizadas correctamente',
        updateError: 'Error al actualizar ubicaciones del rol',
      },
    },
    facilities: {
      searchPlaceholder: 'Buscar ubicaciones...',
      rolesLabel: 'Rol(es)',
      selectRolesPlaceholder: 'Seleccionar roles',
      permissionsSet: 'Permisos asignados',
      noPermissions: 'Sin permisos',
      lastUpdated: (date: string, user: string) =>
        `Última actualización el ${date} por ${user}`,
      noUpdatesYet: 'Sin actualizaciones aún',
      permissionInherited: (roleNames: string) =>
        `El permiso es heredado de ${roleNames} y no se puede cambiar por usuario`,
      noRolesAssigned:
        'No hay roles asignados. Asigne roles para otorgar permisos.',
      emptyState: {
        title: 'Seleccionar una ubicación',
        subtitle: 'Elija una ubicación de la lista para ver o editar permisos',
      },
      messages: {
        updateSuccess: 'Permisos de ubicación actualizados correctamente',
        updateError: 'Error al actualizar permisos de ubicación',
      },
    },
  },
};

const notificationsView = {
  title: 'Preferencias de Notificaciones',
  description:
    'Elija c\u00f3mo desea ser notificado para cada tipo de evento. Las notificaciones cr\u00edticas no se pueden desactivar.',
  empty: 'No hay tipos de notificaciones configurados',
  columns: {
    type: 'Tipo de Notificaci\u00f3n',
  },
  channels: {
    in_app: 'En la app',
    email: 'Correo',
    push: 'Push',
  } as Record<string, string>,
  categories: {
    operations: 'Operaciones',
    trade: 'Comercio',
    finance: 'Finanzas',
    inventory: 'Inventario',
    logistics: 'Log\u00edstica',
    security: 'Seguridad',
  } as Record<string, string>,
  typeLabels: {
    'booking.status_changed': 'Cambio de Estado de Reserva',
    'load.shipped': 'Carga Enviada',
    'load.reconciled': 'Carga Conciliada',
    'load.voided': 'Carga Anulada',
    'load.received': 'Carga Recibida',
    'purchase_order.voided': 'Orden de Compra Anulada',
    'purchase_order.closed': 'Orden de Compra Cerrada',
    'purchase_load.received': 'Carga de Compra Recibida',
    'purchase_load.voided': 'Carga de Compra Anulada',
    'invoice.paid': 'Factura Pagada',
    'invoice.voided': 'Factura Anulada',
    'bill.paid': 'Cuenta Pagada',
    'bill.voided': 'Cuenta Anulada',
    'task.failed': 'Tarea Fallida',
    'task.assigned_to_driver': 'Tarea Asignada a Conductor',
    'task.assigned_to_hauler': 'Tarea Asignada a Transportista',
    'route.failed': 'Ruta Fallida',
    'route.completed': 'Ruta Completada',
    'route.cancelled': 'Ruta Cancelada',
    'user.added_to_organization': 'Usuario Agregado a Organizaci\u00f3n',
    'user_role.assigned': 'Rol Asignado',
    'sales_order.status_changed': 'Cambio de Estado de Orden de Venta',
  } as Record<string, string>,
  messages: {
    saveError: 'Error al guardar la preferencia de notificaci\u00f3n',
    errorMessage: 'Error al cargar las preferencias de notificaciones',
  },
  devSettings: {
    title: 'Configuraci\u00f3n de Desarrollo',
    selfNotify: 'Auto-notificaci\u00f3n',
    selfNotifyDescription:
      'Recibir notificaciones de tus propias acciones. \u00datil para pruebas.',
  },
};

const customerPortal = {
  title: 'Portal de Clientes',
  subtitle: 'Gestionar acceso y permisos de clientes',
  tabs: {
    roles: 'Roles',
    users: 'Usuarios',
  },
  search: {
    placeholder: 'Buscar',
  },
  emptyState: {
    roles: 'No se encontraron roles',
    users: 'No se encontraron clientes',
  },
  actions: {
    addRole: 'Agregar rol',
    inviteCustomer: 'Invitar cliente',
    discard: 'Descartar',
    save: 'Guardar cambios',
    removeAccess: 'Quitar acceso',
    deleteAccount: 'Eliminar cuenta',
    cancelInvite: 'Cancelar invitación',
  },
  userDetails: {
    tabs: {
      contactInfo: 'Info de contacto',
      roles: 'Roles',
      locations: 'Ubicaciones',
    },
    activeSince: (date: string) => `Activo desde ${date}`,
    fields: {
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      company: 'Empresa',
      contactLocation: 'Ubicación de contacto',
    },
    loading: 'Cargando cliente...',
    contactInfo: {
      sectionTitle: 'Info de contacto',
      messages: {
        updateSuccess: 'Cliente actualizado correctamente',
        updateError: 'Error al actualizar cliente',
      },
    },
    roles: {
      facilitiesAssigned: (count: number) =>
        `${count} ${count === 1 ? 'ubicación asignada' : 'ubicaciones asignadas'}`,
      emptyState: {
        title: 'Seleccionar un rol',
        subtitle: 'Elija un rol de la lista para asignar ubicaciones',
      },
      messages: {
        updateSuccess: 'Ubicaciones del rol actualizadas correctamente',
        updateError: 'Error al actualizar ubicaciones del rol',
      },
    },
    locations: {
      title: 'Ubicaciones',
      subtitle: 'El acceso a ubicaciones del cliente se configurará aquí',
    },
    messages: {
      accessRemoved: 'Acceso eliminado',
      accessRemoveError: 'Error al eliminar acceso',
      accountDeleted: 'Cuenta eliminada',
      accountDeleteError: 'Error al eliminar cuenta',
    },
  },
  roles: {
    table: {
      columns: {
        name: 'Nombre del rol',
        users: 'Usuarios',
        status: 'Estado',
        actions: 'Acciones',
      },
      actions: {
        archiveRole: 'Archivar rol',
        restoreRole: 'Restaurar rol',
      },
      aria: 'Tabla de roles externos',
    },
    emptyState: {
      title: 'No se encontraron roles',
      subtitle:
        'No hay roles externos que coincidan con los criterios de búsqueda.',
    },
    details: {
      tabs: {
        permissions: 'Permisos',
        users: 'Usuarios',
      },
      actions: {
        rename: 'Renombrar',
        archive: 'Archivar',
        restore: 'Restaurar',
      },
      users: {
        viewUserSettings: 'Ver configuración del usuario',
        removeFromRole: 'Quitar del rol',
        removeFromRoleDialog: {
          title: (name: string) => `¿Quitar a ${name} de este rol?`,
          description: (name: string) =>
            `Después de confirmar, ${name} será eliminado de este rol. Esta acción se puede deshacer reasignando el rol.`,
          confirm: 'Quitar',
          cancel: 'Cancelar',
          success: 'Usuario eliminado del rol',
          error: 'Error al eliminar usuario del rol',
        },
        bulk: {
          selected: 'seleccionados',
          removeFromRole: 'Quitar del rol',
          resendInvite: 'Reenviar invitación',
          bulkRemoveDialog: {
            title: (count: number) =>
              `¿Quitar ${count} usuario${count === 1 ? '' : 's'} de este rol?`,
            description: (count: number) =>
              `Después de confirmar, ${count} usuario${count === 1 ? '' : 's'} será${count === 1 ? '' : 'n'} eliminado${count === 1 ? '' : 's'} de este rol. Esta acción se puede deshacer reasignando el rol.`,
            confirm: 'Quitar',
            cancel: 'Cancelar',
          },
          removeSuccess: (count: number) =>
            `${count} usuario${count === 1 ? '' : 's'} eliminado${count === 1 ? '' : 's'} del rol`,
          removeError: 'Error al eliminar usuarios del rol',
        },
      },
    },
    renameDialog: {
      title: 'Renombrar rol',
      label: 'Nombre del rol',
      placeholder: 'Ingrese el nombre del rol',
      cancel: 'Cancelar',
      save: 'Guardar',
      success: 'Rol renombrado correctamente',
      error: 'Error al renombrar el rol',
    },
    addRoleDialog: {
      title: 'Crear Rol de Cliente',
      fields: {
        name: {
          label: 'Nombre del rol',
          placeholder: 'Ingrese el nombre del rol',
        },
      },
      actions: {
        cancel: 'Cancelar',
        save: 'Guardar rol',
      },
      messages: {
        success: 'Rol creado correctamente',
        error: 'Error al crear el rol',
      },
    },
    dialogs: {
      archive: {
        title: (name: string) => `¿Archivar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, el rol "${name}" será archivado. Los usuarios asignados a este rol perderán los permisos asociados. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Archivar',
      },
      restore: {
        title: (name: string) => `¿Restaurar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, el rol "${name}" será restaurado y volverá a estar activo. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Restaurar',
      },
      bulkArchive: {
        title: (count: number) =>
          `¿Archivar ${count} rol${count === 1 ? '' : 'es'}?`,
        description: (count: number) =>
          `Tras confirmar, ${count} rol${count === 1 ? '' : 'es'} será${count === 1 ? '' : 'n'} archivado${count === 1 ? '' : 's'}. Los usuarios asignados a estos roles perderán los permisos asociados. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Archivar',
      },
      bulkRestore: {
        title: (count: number) =>
          `¿Restaurar ${count} rol${count === 1 ? '' : 'es'}?`,
        description: (count: number) =>
          `Tras confirmar, ${count} rol${count === 1 ? '' : 'es'} será${count === 1 ? '' : 'n'} restaurado${count === 1 ? '' : 's'} y volverá${count === 1 ? '' : 'n'} a estar activo${count === 1 ? '' : 's'}. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Restaurar',
      },
    },
    bulk: {
      selected: 'seleccionados',
      archive: 'Archivar',
      restore: 'Restaurar',
    },
    addUsersDialog: {
      title: 'Agregar usuario(s) al rol {roleName}',
      description: 'Seleccione usuarios para agregar a este rol',
      steps: {
        users: 'Usuarios',
        facilities: 'Ubicaciones',
      },
      users: {
        header: 'USUARIOS',
        searchPlaceholder: 'Buscar',
      },
      facilities: {
        header: 'UBICACIONES',
        searchPlaceholder: 'Buscar',
      },
      actions: {
        clear: 'Limpiar',
        next: 'Siguiente',
        back: 'Atrás',
        save: 'Guardar',
        addUser: 'Agregar usuario ({count})',
      },
      messages: {
        success: 'Usuarios agregados al rol correctamente',
        error: 'Error al agregar usuarios al rol',
        partialSuccess:
          '{successCount} de {totalCount} usuarios agregados correctamente',
      },
    },
  },
  users: {
    selected: 'seleccionados',
    table: {
      columns: {
        name: 'Nombre',
        contactInfo: 'Información de contacto',
        status: 'Estado',
      },
      aria: 'Tabla de usuarios del portal de clientes',
    },
    actions: {
      cancelInvite: 'Cancelar invitación',
      removeAccess: 'Quitar acceso',
      delete: 'Eliminar',
    },
    emptyState: {
      title: 'No se encontraron clientes',
      subtitle: 'No hay clientes que coincidan con los criterios de búsqueda.',
    },
    dialogs: {
      cancelInvite: {
        title: (name: string) => `¿Cancelar invitación para ${name}?`,
        description: (name: string) =>
          `Tras confirmar, la invitación para ${name} será cancelada. ¿Desea continuar?`,
        discard: 'Descartar',
        confirm: 'Cancelar invitación',
      },
      removeAccess: {
        title: (name: string) => `¿Quitar acceso a ${name}?`,
        description: (name: string) =>
          `Tras confirmar, ${name} perderá acceso al portal de clientes. ¿Desea continuar?`,
        discard: 'Descartar',
        remove: 'Quitar',
      },
      deleteAccount: {
        title: (name: string) => `¿Eliminar ${name}?`,
        description: (name: string) =>
          `Tras confirmar, ${name} será eliminado y dejará de tener acceso. Esta acción no se puede deshacer.`,
        discard: 'Descartar',
        delete: 'Eliminar',
      },
    },
    messages: {
      invitationCancelled: 'Invitación cancelada',
      invitationCancelError: 'Error al cancelar invitación',
      accessRemoved: 'Acceso eliminado',
      accessRemoveError: 'Error al eliminar acceso',
      customerRemoved: 'Cliente eliminado',
      customerRemoveError: 'Error al eliminar cliente',
    },
  },
  userStatus: {
    active: 'Activo',
    inviteSent: 'Invitación enviada',
  },
  inviteCustomerDialog: {
    title: 'Invitar cliente',
    fields: {
      account: {
        label: 'Cuenta',
        placeholder: 'Seleccionar una cuenta',
      },
      email: {
        label: 'Correo electrónico',
        placeholder: 'Dirección de correo',
      },
    },
    actions: {
      cancel: 'Cancelar',
      sendInvite: 'Enviar invitación',
    },
    messages: {
      success: 'Invitación enviada correctamente',
      error: 'Error al enviar la invitación',
    },
  },
};

const hardwareView = {
  sidebar: {
    title: 'Hardware',
  },
  sections: {
    sidecars: 'Sidecars',
    cameras: 'Cámaras',
    scanners: 'Escáneres',
    printers: 'Impresoras',
    scales: 'Balanzas',
    ustudio: 'U Studio',
  },
  routeLabels: {
    hardware: 'Hardware',
    registerSidecar: 'Registrar Sidecar',
    sidecarDetails: 'Detalles del Sidecar',
    cameraDetails: 'Detalles de la Cámara',
    scannerDetails: 'Detalles del Escáner',
    printerDetails: 'Detalles de la Impresora',
    scaleDetails: 'Detalles de la Balanza',
  },
  common: {
    errorLabel: 'Error',
    unknownError: 'Error desconocido',
    confirmDelete: 'Eliminar',
    confirmWipe: 'Limpiar',
    cancel: 'Cancelar',
  },
  sidecarsPanel: {
    columns: {
      name: 'Nombre',
      status: 'Estado',
      operatingSystem: 'Sistema Operativo',
      facility: 'Instalación',
      localIpAddress: 'IP Local',
      created: 'Creado',
    },
    statusLabels: {
      Pending: 'Pendiente',
      Online: 'En Línea',
      Offline: 'Desconectado',
    },
    search: {
      placeholder: 'Buscar sidecars...',
      ariaLabel: 'Buscar sidecars',
    },
    emptyState: {
      title: 'No se encontraron sidecars',
      description:
        'Los sidecars aparecen aquí después de ser registrados por el sistema.',
    },
    connectionLabels: {
      local: 'Local',
      relayed: 'Retransmitido',
    },
    actions: {},
    errors: {
      loadingFailed: (message: string) =>
        `Error al cargar sidecars: ${message}`,
    },
    ariaLabel: 'Lista de sidecars',
  },
  sidecarDetailView: {
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
      disconnected: 'Desconectado',
      connecting: 'Conectando',
    },
    sections: {
      systemInfo: 'Información del Sistema',
      security: 'Seguridad',
      location: 'Ubicación',
      deviceDiscovery: 'Descubrimiento de Dispositivos',
      network: 'Red',
      timestamps: 'Marcas de Tiempo',
    },
    fields: {
      name: 'Nombre',
      operatingSystem: 'Sistema Operativo',
      operatingSystemVersion: 'Versión del SO',
      version: 'Versión del Sidecar',
      publicKey: 'Clave Pública',
      facility: 'Instalación',
      localIpAddress: 'Dirección IP Local',
      remoteIpAddressV4: 'Dirección IPv4 Remota',
      remoteIpAddressV6: 'Dirección IPv6 Remota',
      linkSpeedUpstream: 'Velocidad de Subida',
      linkSpeedDownstream: 'Velocidad de Bajada',
      latencyMs: 'Latencia',
      jitterMs: 'Fluctuación',
      discoveryEnabled: 'Descubrimiento de Dispositivos',
      created: 'Creado',
      updated: 'Actualizado',
      connectedAt: 'Conectado',
      lastHeartbeatAt: 'Último Latido',
    },
    descriptions: {
      discoveryEnabled:
        'Descubrir y registrar automáticamente cámaras, escáneres, impresoras y balanzas conectadas a este sidecar.',
    },
    placeholders: {
      name: 'ej., PROD-SRV-01',
      operatingSystem: 'Seleccionar sistema operativo...',
      operatingSystemVersion: 'ej., Ubuntu 22.04',
      facility: 'Seleccionar instalación...',
    },
    tabs: {
      details: 'Detalles',
      logs: 'Registros',
      uploadQueue: 'Cola de Subida',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToHardware: 'Volver a Hardware',
      downloadLatest: (version: string) => `Descargar v${version}`,
      checkForUpdate: 'Buscar Actualización',
      checkingForUpdate: 'Buscando...',
      syncCameras: 'Sincronizar Cámaras',
      syncScanners: 'Sincronizar Escáneres',
      syncPrinters: 'Sincronizar Impresoras',
      syncScales: 'Sincronizar Balanzas',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Actualizaciones',
    },
    units: {
      mbps: 'Mbps',
      ms: 'ms',
    },
    logs: {
      refresh: 'Actualizar',
      wipeLogs: 'Borrar Registros',
      wipeConfirmation:
        '¿Está seguro de que desea borrar todos los registros? Esta acción no se puede deshacer.',
      noLogs: 'No hay registros disponibles',
      offline:
        'El sidecar está desconectado. Conéctese para ver los registros.',
      streamErrorFallback: 'Error al transmitir registros',
      wipeErrorFallback: 'Error al borrar registros',
    },
    uploadQueue: {
      refresh: 'Actualizar',
      delete: 'Eliminar',
      deleteConfirmation:
        '¿Está seguro de que desea eliminar esta entrada de la cola?',
      empty: 'La cola de subida está vacía',
      offline: 'El sidecar está desconectado. Conéctese para ver la cola.',
      columns: {
        id: 'ID',
        cameraId: 'ID de Cámara',
        storageKey: 'Clave de Almacenamiento',
        contentType: 'Tipo de Contenido',
        fileSize: 'Tamaño de Archivo',
        enqueuedAt: 'En Cola Desde',
      },
      errors: {
        listEntriesFallback: 'Error al listar entradas de la cola',
        deleteEntryFallback: 'Error al eliminar la entrada',
      },
      fileSizeUnits: {
        bytes: 'B',
        kilobytes: 'KB',
        megabytes: 'MB',
      },
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar este sidecar? Esta acción no se puede deshacer.',
      deleteSuccess: 'Sidecar eliminado exitosamente',
      deleteError: (message: string) => `Error al eliminar sidecar: ${message}`,
      sidecarNotFound: 'Sidecar no encontrado',
      unsavedDataMessage:
        'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Sidecar actualizado exitosamente',
      updateError: (message: string) =>
        `Error al actualizar sidecar: ${message}`,
      updateCheckTriggered: 'Verificación de actualización iniciada',
      updateCheckError: (message: string) =>
        `Error al iniciar verificación de actualización: ${message}`,
      syncFailed: (error: string) => `Error al sincronizar: ${error}`,
    },
  },
  sidecarRegisterView: {
    title: 'Registrar Sidecar',
    subtitle: 'Complete el registro de un nuevo dispositivo sidecar.',
    sections: {
      deviceInformation: 'Información del Dispositivo',
      configuration: 'Configuración',
    },
    fields: {
      sidecarId: 'ID del Sidecar',
      publicKey: 'Clave Pública',
      facility: 'Instalación',
      name: 'Nombre',
    },
    placeholders: {
      facility: 'Seleccionar instalación...',
      name: 'ej., PROD-SRV-01',
    },
    actions: {
      register: 'Registrar Sidecar',
      registering: 'Registrando...',
      backToHardware: 'Volver a Hardware',
    },
    messages: {
      missingParams:
        'Faltan parámetros de registro. Por favor inicie el registro desde la terminal del sidecar.',
      success: 'Sidecar registrado exitosamente. Puede cerrar esta página.',
      error: (message: string) => `Error en el registro: ${message}`,
    },
  },
  camerasPanel: {
    columns: {
      name: 'Nombre',
      driverType: 'Controlador',
      ipAddress: 'Dirección IP',
      status: 'Estado',
      sidecar: 'Sidecar',
      created: 'Creado',
    },
    statusLabels: {
      [enumCameraStatus.Discovered]: 'Descubierta',
      [enumCameraStatus.Configured]: 'Configurada',
    },
    phaseLabels: {
      discovered: 'Descubierta',
      provisioning: 'Aprovisionando',
      wrong_credentials: 'Credenciales Incorrectas',
      operational: 'Operativa',
    },
    search: {
      placeholder: 'Buscar cámaras...',
      ariaLabel: 'Buscar cámaras',
    },
    emptyState: {
      title: 'No se encontraron cámaras',
      description:
        'Las cámaras aparecen aquí después de ser descubiertas por un sidecar.',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
    },
    actions: {
      addRtspCamera: 'Agregar Cámara RTSP',
    },
    errors: {
      loadingFailed: (message: string) => `Error al cargar cámaras: ${message}`,
    },
    ariaLabel: 'Lista de cámaras',
  },
  scannersPanel: {
    columns: {
      name: 'Nombre',
      driverType: 'Controlador',
      status: 'Estado',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      [enumScannerStatus.Discovered]: 'Descubierto',
    },
    search: {
      placeholder: 'Buscar escáneres...',
      ariaLabel: 'Buscar escáneres',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectado',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
    },
    emptyState: {
      title: 'No se encontraron escáneres',
      description:
        'Los escáneres aparecen aquí después de ser detectados por un sidecar.',
    },
    ariaLabel: 'Lista de escáneres',
  },
  printersPanel: {
    columns: {
      name: 'Nombre',
      printerType: 'Tipo',
      driverType: 'Controlador',
      status: 'Estado',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      [enumPrinterStatus.Discovered]: 'Descubierta',
    },
    search: {
      placeholder: 'Buscar impresoras...',
      ariaLabel: 'Buscar impresoras',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
    },
    emptyState: {
      title: 'No se encontraron impresoras',
      description:
        'Las impresoras aparecen aquí después de ser detectadas por un sidecar.',
    },
    ariaLabel: 'Lista de impresoras',
  },
  scalesPanel: {
    columns: {
      name: 'Nombre',
      formatType: 'Formato',
      connectionType: 'Conexión',
      status: 'Estado',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      Discovered: 'Descubierta',
      Configured: 'Configurada',
      Active: 'Activa',
    },
    search: {
      placeholder: 'Buscar balanzas...',
      ariaLabel: 'Buscar balanzas',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
    },
    emptyState: {
      title: 'No se encontraron balanzas',
      description:
        'Las balanzas aparecen aquí después de ser configuradas en un sidecar.',
    },
    actions: {
      addScale: 'Agregar Balanza',
    },
    ariaLabel: 'Lista de balanzas',
  },
  scaleCreateDialog: {
    title: 'Agregar Balanza',
    fields: {
      name: 'Nombre de la Balanza',
      connectionType: 'Tipo de Conexión',
      connectionTypeOptions: {
        TCP: 'TCP',
        Serial: 'Serie (USB)',
      },
      tcpHost: 'Host TCP',
      tcpHostPlaceholder: '192.168.1.100',
      tcpPort: 'Puerto TCP',
      tcpPortPlaceholder: '4001',
      serialPortPath: 'Puerto Serie',
      serialPortPathPlaceholder: '/dev/ttyUSB0',
      baudRate: 'Velocidad en Baudios',
      dataBits: 'Bits de Datos',
      stopBits: 'Bits de Parada',
      parity: 'Paridad',
      scaleFormatType: 'Formato de Balanza',
      scaleFormatTypePlaceholder: 'Toledo',
      measurementMode: 'Modo de Medición',
      measurementModeOptions: {
        Continuous: 'Continuo',
        OnDemand: 'Bajo Demanda',
      },
      onDemandString: 'Cadena Bajo Demanda',
      unitOfMeasurement: 'Unidad de Medida',
      unitOfMeasurementPlaceholder: 'Auto-detectar desde balanza',
      unitOptions: {
        lb: 'Libras (lb)',
        kg: 'Kilogramos (kg)',
      },
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Seleccionar un sidecar...',
      camera: 'Cámara',
      cameraPlaceholder: 'Seleccionar una cámara (opcional)...',
    },
    actions: {
      cancel: 'Cancelar',
      create: 'Crear',
      creating: 'Creando...',
    },
    messages: {
      success: 'Balanza creada exitosamente',
      error: (message: string) => `Error al crear balanza: ${message}`,
    },
  },
  scaleDetailView: {
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
      disconnected: 'Desconectado',
      connecting: 'Conectando',
    },
    statusLabels: {
      Discovered: 'Descubierta',
      Configured: 'Configurada',
      Active: 'Activa',
    },
    sections: {
      scaleInfo: 'Información de la Balanza',
      configuration: 'Configuración de Balanza',
      connectionSettings: 'Configuración de Conexión',
      associatedCamera: 'Cámara Asociada',
      connection: 'Conexión',
      timestamps: 'Marcas de Tiempo',
      liveWeight: 'Peso en Vivo',
    },
    fields: {
      name: 'Nombre',
      connectionType: 'Tipo de Conexión',
      scaleFormatType: 'Formato de Balanza',
      measurementMode: 'Modo de Medición',
      serialPortPath: 'Puerto Serie',
      baudRate: 'Velocidad en Baudios',
      dataBits: 'Bits de Datos',
      stopBits: 'Bits de Parada',
      parity: 'Paridad',
      tcpHost: 'Host TCP',
      tcpPort: 'Puerto TCP',
      onDemandString: 'Cadena Bajo Demanda',
      unitOfMeasurement: 'Unidad de Medida',
      camera: 'Cámara',
      cameraPlaceholder: 'Seleccionar una cámara...',
      noCamera: 'Ninguna',
      sidecar: 'Sidecar',
      lastSeenAt: 'Última Vez Visto',
      created: 'Creado',
      updated: 'Actualizado',
      grossWeight: 'Peso Bruto',
      tareWeight: 'Tara',
      netWeight: 'Peso Neto',
      stability: 'Estabilidad',
    },
    labels: {
      stable: 'Estable',
      unstable: 'Inestable',
    },
    tabs: {
      info: 'Información',
      weight: 'Peso',
    },
    actions: {
      backToHardware: 'Volver a Hardware',
      refreshWeight: 'Actualizar Peso',
      edit: 'Editar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      deleting: 'Eliminando...',
    },
    messages: {
      scaleNotFound: 'Balanza no encontrada',
      deleteConfirmation:
        '¿Estás seguro de que deseas eliminar esta balanza? Esta acción no se puede deshacer.',
      deleteSuccess: 'Balanza eliminada exitosamente',
      deleteError: (message: string) => `Error al eliminar balanza: ${message}`,
      offline: 'La balanza está desconectada',
      connecting: 'Conectando al sidecar...',
      noWeightYet: 'Sin peso aún',
      weightReadFailed: 'No se pudo leer el peso actual',
      weightStreamFailed: 'No se pudo suscribir a actualizaciones de peso',
      unsavedDataMessage:
        '¿Tienes cambios sin guardar. Estás seguro de que deseas descartarlos?',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Cambios',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Balanza actualizada exitosamente',
      updateError: (message: string) =>
        `Error al actualizar balanza: ${message}`,
    },
  },
  cameraDetailView: {
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
      disconnected: 'Desconectado',
      connecting: 'Conectando',
    },
    sections: {
      cameraInfo: 'Información de la Cámara',
      configuration: 'Configuración de Cámara',
      deviceInfo: 'Información del Dispositivo',
      network: 'Red',
      setup: 'Configuración',
      initialization: 'Inicialización',
      credentials: 'Credenciales',
      stream: 'Transmisión',
      timestamps: 'Marcas de Tiempo',
    },
    fields: {
      name: 'Nombre',
      uniqueId: 'ID Único',
      driverType: 'Tipo de Controlador',
      ipAddress: 'Dirección IP',
      manufacturer: 'Fabricante',
      model: 'Modelo',
      firmwareVersion: 'Versión de Firmware',
      status: 'Estado',
      lastSeenAt: 'Última Vez Visto',
      sidecar: 'Sidecar',
      username: 'Usuario',
      password: 'Contraseña',
      created: 'Creado',
      updated: 'Actualizado',
      rtspStatus: 'Estado RTSP',
      rtspPort: 'Puerto RTSP',
      httpStatus: 'Estado HTTP',
      httpPort: 'Puerto HTTP',
      onvifStatus: 'Estado ONVIF',
      onvifPort: 'Puerto ONVIF',
      streamProfile: 'Perfil de Transmisión',
      streamUrl: 'URL de Transmisión',
      snapshotUrl: 'URL de Captura',
    },
    tabs: {
      details: 'Detalles',
      liveFeed: 'Transmisión en Vivo',
    },
    actions: {
      delete: 'Eliminar',
      deleting: 'Eliminando...',
      backToHardware: 'Volver a Hardware',
      setCredentials: 'Establecer Credenciales',
      settingCredentials: 'Estableciendo...',
      initialize: 'Inicializar Cámara',
      initializing: 'Inicializando...',
      enableHttp: 'Habilitar HTTP',
      enablingHttp: 'Habilitando...',
      enableRtsp: 'Habilitar RTSP',
      enablingRtsp: 'Habilitando...',
      enableOnvif: 'Habilitar ONVIF',
      enablingOnvif: 'Habilitando...',
      cancel: 'Cancelar',
      edit: 'Editar',
      save: 'Guardar',
      saving: 'Guardando...',
    },
    configuration: {
      profileSub: 'Tiempo Real (Sub Stream)',
      profileMain: 'Alta Resolución (Main Stream)',
    },
    liveFeed: {
      connecting: 'Conectando al sidecar...',
      connectToSidecar: 'Conéctese al sidecar para ver la transmisión en vivo',
      cameraOffline: 'La cámara está desconectada',
      noLiveFeedSupport: 'La cámara no admite transmisión en vivo',
      startingStream: 'Iniciando transmisión...',
      streamStartFailed: 'Error al iniciar la transmisión',
      loadingSnapshot: 'Cargando captura...',
      streamProfile: 'Calidad de Transmisión',
      profileMain: 'Alta Resolución (transcodificado, ~2s latencia)',
      profileSub: 'Tiempo real (896x512)',
      webrtcConnectionFailed:
        'El video en vivo requiere una conexión de red directa. PTZ y capturas siguen disponibles.',
      snapshotFallback: 'Mostrando vista previa de captura',
    },
    ptz: {
      title: 'Controles PTZ',
      up: 'Arriba',
      down: 'Abajo',
      left: 'Izquierda',
      right: 'Derecha',
      zoomIn: 'Acercar',
      zoomOut: 'Alejar',
    },
    floodlight: {
      title: 'Reflector',
      on: 'Encender',
      off: 'Apagar',
      brightness: 'Brillo',
      brightnessValue: (value: number) => `${value}%`,
    },
    siren: {
      title: 'Sirena',
      trigger: 'Activar Sirena',
      confirmTitle: '¿Activar Sirena?',
      confirmMessage: 'Esto activará la alarma de sirena de la cámara.',
      confirm: 'Activar',
      cancel: 'Cancelar',
    },
    snapshot: {
      title: 'Captura',
      overlayEditorTitle: 'Capas de Captura',
      capture: 'Tomar Captura',
      capturing: 'Capturando...',
      addTextLayer: 'Agregar Capa de Texto',
      addImageLayer: 'Agregar Capa de Imagen',
      removeLayer: 'Eliminar Capa',
      moveLayerUp: 'Subir Capa',
      moveLayerDown: 'Bajar Capa',
      noLayers: 'No hay capas de superposición configuradas.',
      layerTypeLabel: 'Tipo de Capa',
      anchorLabel: 'Anclaje',
      textLabel: 'Texto',
      fontSizeLabel: 'Tamaño de Fuente',
      colorLabel: 'Color',
      uploadImageLabel: 'Imagen de Superposición',
      clearImage: 'Limpiar Imagen',
      imageDataUrlLabel: 'Data URL de la Imagen',
      imageDataUrlPlaceholder:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
      download: 'Descargar',
      dismiss: 'Cerrar',
      channel: (channel: number) => `Canal ${channel}`,
      imageAlt: (channel: number) => `Captura del canal ${channel}`,
      captureErrorFallback: 'Error al capturar imagen',
      layerTypes: {
        text: 'Texto',
        image: 'Imagen',
      },
      anchors: {
        topLeft: 'Arriba Izquierda',
        topRight: 'Arriba Derecha',
        bottomLeft: 'Abajo Izquierda',
        bottomRight: 'Abajo Derecha',
        fullFrame: 'Marco Completo',
      },
      errors: {
        emptyTextLayer: 'Las capas de texto requieren contenido.',
        invalidImageDataUrl:
          'Las capas de imagen requieren un data URL base64 válido.',
        unsupportedImageType:
          'Las capas de imagen solo admiten data URLs PNG y JPEG.',
      },
    },
    messages: {
      deleteConfirmation:
        '¿Está seguro de que desea eliminar esta cámara? Esta acción no se puede deshacer.',
      deleteSuccess: 'Cámara eliminada exitosamente',
      deleteError: (message: string) => `Error al eliminar cámara: ${message}`,
      cameraNotFound: 'Cámara no encontrada',
      credentialsSuccess: 'Credenciales actualizadas exitosamente',
      credentialsError: (message: string) =>
        `Error al establecer credenciales: ${message}`,
      initializationRequired:
        'Esta cámara está en estado de fábrica. Inicialícela para establecer una contraseña segura y habilitar la transmisión RTSP.',
      sidecarNotConnected:
        'El sidecar no está conectado. La inicialización requiere una conexión activa al sidecar.',
      initializeSuccess: 'Cámara inicializada exitosamente',
      initializeError: (message: string) =>
        `Error al inicializar cámara: ${message}`,
      enableHttpSuccess: 'HTTP habilitado exitosamente',
      enableHttpError: (message: string) =>
        `Error al habilitar HTTP: ${message}`,
      enableRtspSuccess: 'RTSP habilitado exitosamente',
      enableRtspError: (message: string) =>
        `Error al habilitar RTSP: ${message}`,
      enableOnvifSuccess: 'ONVIF habilitado exitosamente',
      enableOnvifError: (message: string) =>
        `Error al habilitar ONVIF: ${message}`,
      streamProfileSuccess: 'Perfil de transmisión actualizado exitosamente',
      streamProfileError: (message: string) =>
        `Error al actualizar perfil de transmisión: ${message}`,
      streamUpdateSuccess: 'URLs de transmisión actualizadas exitosamente',
      streamUpdateError: (message: string) =>
        `Error al actualizar URLs de transmisión: ${message}`,
      wrongCredentialsWarning:
        'Las credenciales de la cámara son incorrectas. Actualice las credenciales para reconectar.',
      wrongCredentialsLiveFeed:
        'Las credenciales de la cámara son incorrectas. Actualice las credenciales en la pestaña de Detalles.',
      cameraSetupInProgress:
        'La cámara se está configurando. La transmisión en vivo estará disponible una vez que se complete la configuración.',
      unsavedDataMessage:
        '¿Tienes cambios sin guardar. Estás seguro de que deseas descartarlos?',
    },
    httpStatus: {
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
    },
    rtspStatus: {
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
    },
    onvifStatus: {
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Cambios',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Cámara actualizada exitosamente',
      updateError: (message: string) =>
        `Error al actualizar cámara: ${message}`,
    },
  },
  demoCreateDialog: {
    titles: {
      scale: 'Agregar Balanza Demo',
      printer: 'Agregar Impresora Demo',
      scanner: 'Agregar Escáner Demo',
      camera: 'Agregar Cámara Demo',
    },
    fields: {
      name: 'Nombre',
      printerType: 'Tipo de Impresora',
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Seleccionar un sidecar...',
    },
    printerTypeOptions: {
      Standard: 'Estándar',
      Thermal: 'Térmica',
    },
    actions: {
      cancel: 'Cancelar',
      create: 'Crear',
      creating: 'Creando...',
      addDemoScale: 'Agregar Balanza Demo',
      addDemoPrinter: 'Agregar Impresora Demo',
      addDemoScanner: 'Agregar Escáner Demo',
      addDemoCamera: 'Agregar Cámara Demo',
    },
    messages: {
      success: 'Dispositivo demo creado exitosamente',
      error: (message: string) => `Error al crear dispositivo demo: ${message}`,
    },
  },
  rtspCameraCreateDialog: {
    title: 'Agregar Cámara RTSP',
    fields: {
      name: 'Nombre de la Cámara',
      streamUrl: 'URL de Transmisión',
      streamUrlPlaceholder: 'rtsp://192.168.1.100:554/stream',
      snapshotUrl: 'URL de Captura (opcional)',
      snapshotUrlPlaceholder: 'rtsp://192.168.1.100:554/main',
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Seleccionar un sidecar...',
    },
    actions: {
      cancel: 'Cancelar',
      create: 'Crear',
      creating: 'Creando...',
    },
    messages: {
      success: 'Cámara RTSP creada exitosamente',
      error: (message: string) => `Error al crear cámara: ${message}`,
    },
  },
  scannerDetailView: {
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
      disconnected: 'Desconectado',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectado',
      needsSetup: 'Necesita Configuración',
    },
    sections: {
      scannerInfo: 'Información del Escáner',
      configuration: 'Configuración de Escáner',
      deviceInfo: 'Información del Dispositivo',
      connection: 'Conexión',
      timestamps: 'Marcas de Tiempo',
      scan: 'Escaneo',
      scanResult: 'Resultado de Escaneo',
      images: 'Imágenes',
      aamvaFields: 'Campos AAMVA',
    },
    formats: {
      aamva: 'AAMVA',
      raw: 'RAW',
    },
    fields: {
      name: 'Nombre',
      scannerId: 'ID del Escáner',
      uniqueId: 'ID Único',
      driverType: 'Tipo de Controlador',
      manufacturer: 'Fabricante',
      model: 'Modelo',
      firmwareVersion: 'Versión de Firmware',
      status: 'Estado',
      sidecar: 'Sidecar',
      created: 'Creado',
      updated: 'Actualizado',
      format: 'Formato',
      timeout: 'Tiempo de Espera',
      elapsedMs: 'Tiempo Transcurrido (ms)',
    },
    scanResultLabels: {
      rawPayload: 'Carga útil sin procesar',
      pdf417Payload: 'Carga útil PDF417',
      pdf417Decoded: 'PDF417 decodificado',
      frontImageLabel: 'Etiqueta de imagen frontal',
      backImageLabel: 'Etiqueta de imagen trasera',
      portraitImageLabel: 'Etiqueta de imagen de retrato',
      parsedFields: (count: number) => `Campos analizados (${count})`,
      noParsedFields: 'No hay campos analizados',
      ocrText: 'Texto OCR',
      diagnostics: 'Diagnósticos',
      yes: 'sí',
      no: 'no',
      bytes: (count: number) => `${count} bytes`,
      sourceLabels: {
        pdf417: 'PDF417',
        ocr: 'OCR',
      },
    },
    tabs: {
      info: 'Información',
      test: 'Prueba',
    },
    actions: {
      backToHardware: 'Volver a Hardware',
      scanNow: 'Escanear Ahora',
      scanning: 'Escaneando...',
      download: 'Descargar',
      delete: 'Eliminar',
      deleting: 'Eliminando...',
    },
    messages: {
      scannerNotFound: 'Escáner no encontrado',
      deleteConfirmation:
        '¿Estás seguro de que deseas eliminar este escáner? Esta acción no se puede deshacer.',
      deleteSuccess: 'Escáner eliminado exitosamente',
      deleteError: (message: string) => `Error al eliminar escáner: ${message}`,
      unknownScanError: 'Error de escaneo desconocido',
      unsavedDataMessage:
        '¿Tienes cambios sin guardar. Estás seguro de que deseas descartarlos?',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Cambios',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Escáner actualizado exitosamente',
      updateError: (message: string) =>
        `Error al actualizar escáner: ${message}`,
    },
  },
  printerDetailView: {
    connectionLabels: {
      direct: 'Directo',
      relayed: 'Retransmitido',
      disconnected: 'Desconectado',
    },
    presenceLabels: {
      online: 'En Línea',
      offline: 'Desconectada',
    },
    sections: {
      printerInfo: 'Información de la Impresora',
      configuration: 'Configuración de Impresora',
      deviceInfo: 'Información del Dispositivo',
      connection: 'Conexión',
      timestamps: 'Marcas de Tiempo',
    },
    fields: {
      name: 'Nombre',
      printerId: 'ID de la Impresora',
      uniqueId: 'ID Único',
      driverType: 'Tipo de Controlador',
      manufacturer: 'Fabricante',
      model: 'Modelo',
      firmwareVersion: 'Versión de Firmware',
      printerType: 'Tipo de Impresora',
      pageSize: 'Tamaño de Página',
      rollWidth: 'Ancho del Rollo',
      status: 'Estado',
      sidecar: 'Sidecar',
      created: 'Creado',
      updated: 'Actualizado',
    },
    printerTypeLabels: {
      Standard: 'Estándar',
      Thermal: 'Térmica',
    },
    pageSizeLabels: {
      Letter: 'Letter',
      A4: 'A4',
      Legal: 'Legal',
    },
    rollWidthLabels: {
      Roll58mm: '58mm',
      Roll80mm: '80mm',
    },
    queueColumns: {
      documentName: 'Documento',
      status: 'Estado',
      pages: 'Páginas',
      size: 'Tamaño',
    },
    tabs: {
      info: 'Información',
      queue: 'Cola de Impresión',
    },
    actions: {
      backToHardware: 'Volver a Hardware',
      printTestPage: 'Imprimir Página de Prueba',
      cancelJob: 'Cancelar',
      refresh: 'Actualizar',
      edit: 'Editar',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      deleting: 'Eliminando...',
    },
    messages: {
      printerNotFound: 'Impresora no encontrada',
      testPageSent: 'Página de prueba enviada a la impresora',
      jobCancelled: 'Trabajo de impresión cancelado',
      queueEmpty: 'No hay trabajos en la cola de impresión',
      notConnected: 'No conectado al sidecar',
      deleteConfirmation:
        '¿Estás seguro de que deseas eliminar esta impresora? Esta acción no se puede deshacer.',
      deleteSuccess: 'Impresora eliminada exitosamente',
      deleteError: (message: string) =>
        `Error al eliminar impresora: ${message}`,
      unsavedDataMessage:
        '¿Tienes cambios sin guardar. Estás seguro de que deseas descartarlos?',
    },
    footer: {
      discard: 'Descartar',
      saveUpdates: 'Guardar Cambios',
    },
    toast: {
      discarded: 'Cambios descartados',
      updated: 'Impresora actualizada exitosamente',
      updateError: (message: string) =>
        `Error al actualizar impresora: ${message}`,
    },
  },
};

const ustudioView = {
  preferences: {
    title: 'Preferencias',
    enabled: 'Habilitar U Studio',
    brightness: 'Brillo',
    save: 'Guardar',
    discard: 'Descartar',
  },
  debug: {
    title: 'Depuración',
    connect: 'Conectar',
    disconnect: 'Desconectar',
    status: 'Estado',
    deviceInfo: 'Info del Dispositivo',
    eventLog: 'Registro de Eventos',
    buttons: 'botones',
    framebuffer: 'framebuffer',
    noEvents: 'Sin eventos aún',
    webUsbUnavailable:
      'WebUSB no está disponible en este navegador. Usa Chrome u otro navegador basado en Chromium.',
  },
  status: {
    disconnected: 'Desconectado',
    connecting: 'Conectando',
    connected: 'Conectado',
    error: 'Error',
  },
};

const automationsView = {
  title: 'Automatizaciones',
  fields: {
    executionTimeout: 'Tiempo de ejecución (segundos)',
    executionTimeoutDescription:
      'Tiempo máximo en segundos que una ejecución de automatización puede funcionar antes de ser cancelada.',
    maxRetryCount: 'Número máximo de reintentos',
    maxRetryCountDescription:
      'Número máximo de veces que se reintentará una acción fallida.',
  },
  comingSoon:
    'La configuración de automatizaciones estará disponible próximamente.',
  actions: {
    save: 'Guardar cambios',
  },
};

const settingsLayout = {
  title: 'Configuración',
  search: 'Buscar',
  notImplemented: 'Aún no implementado',
  groupTitles: {
    personal: 'Personal',
    organization: 'Organización',
    modules: 'Módulos',
    compliance: 'Cumplimiento',
  },
  routeLabels: {
    profile: 'Perfil',
    accessAndSecurity: 'Acceso y Seguridad',
    preferences: 'Preferencias',
    notifications: 'Notificaciones',
    locations: 'Ubicaciones',
    facilityDetails: 'Detalles de la Instalación',
    groupDetails: 'Detalles del Grupo',
    teamAndPermissions: 'Equipo y Permisos',
    userDetails: 'Detalles del Usuario',
    roleDetails: 'Detalles del Rol',
    inventory: 'Inventario',
    itemDetails: 'Detalles del Artículo',
    itemGroupDetails: 'Detalles del Grupo de Artículos',
    itemAliasDetails: 'Detalles del Alias del Artículo',
    categoryDetails: 'Detalles de la Categoría',
    hardware: 'Hardware',
    registerSidecar: 'Registrar Sidecar',
    sidecarDetails: 'Detalles del Sidecar',
    cameraDetails: 'Detalles de la Cámara',
    scannerDetails: 'Detalles del Escáner',
    printerDetails: 'Detalles de la Impresora',
    scaleDetails: 'Detalles de la Balanza',
    sales: 'Ventas',
    finance: 'Finanzas',
    ruleConfiguration: 'Configuración de Reglas',
    facilityRules: 'Reglas de la Instalación',
    doNotBuyList: 'Lista de No Comprar',
    govReporting: 'Reportes Gubernamentales',
    configDetail: 'Detalle de Configuración',
    itemCodes: 'Códigos de Artículos',
    dispatchHistory: 'Historial de Despachos',
    customAttributes: 'Atributos Personalizados',
    customerPortal: 'Portal de Clientes',
    customerDetails: 'Detalles del Cliente',
  },
};

export const es = {
  automationsView,
  complianceView,
  customerPortal,
  customAttributesView,
  hardwareView,
  inventoryView,
  jsonView,
  notificationsView,
  preferencesView,
  settingsLayout,
  settingsView,
  teamAndPermissions,
  ustudioView,
};
